import React, { useState, useEffect } from 'react';
import { MapPin, Plus, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { poiService, POI } from '../services/poiService';
import { useAuth } from '../contexts/AuthContext';

interface MyPOIsPanelProps {
  onCreatePOI: () => void;
  onSelectPOI: (poi: POI) => void;
  userLocation?: { latitude: number; longitude: number } | null;
}

const MyPOIsPanel: React.FC<MyPOIsPanelProps> = ({ onCreatePOI, onSelectPOI, userLocation }) => {
  const { user } = useAuth();
  const [pois, setPois] = useState<POI[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadUserPOIs();
    }
  }, [user, userLocation]);

  const loadUserPOIs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Use user's location if available, otherwise use a default location
      // Default to Bucharest coordinates if no location is provided
      const lat = userLocation?.latitude || 44.4268;
      const lng = userLocation?.longitude || 26.1025;
      const radiusMeters = 100000; // 100km radius to capture user's POIs
      
      // Fetch POIs with location parameters (backend requires this)
      // For authenticated users, the backend returns their pending POIs in addition to validated ones
      const response = await poiService.fetchPOIs({
        latitude: lat,
        longitude: lng,
        radius: radiusMeters
      });
      
      // Filter to only show POIs created by the current user
      const userPOIs = response.data.filter((poi: POI) => {
        // Handle both string and number IDs
        const poiCreatorId = poi.created_by?.toString();
        const userId = user?.id?.toString();
        return poiCreatorId === userId;
      });
      setPois(userPOIs);
    } catch (err: any) {
      console.error('Error loading user POIs:', err);
      setError('Failed to load your POIs');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'VALIDATED':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'PENDING':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'REJECTED':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'VALIDATED':
        return 'Validated';
      case 'PENDING':
        return 'Pending';
      case 'REJECTED':
        return 'Rejected';
      default:
        return status;
    }
  };

  return (
    <div className="w-80 bg-creator-surface dark:bg-gray-900 rounded-2xl shadow-lg p-5 border border-creator-border dark:border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-creator-text dark:text-gray-100">My POIs</h2>
        <button
          onClick={onCreatePOI}
          className="p-2 bg-creator-accent hover:bg-opacity-90 text-white rounded-lg transition-all shadow-sm"
          title="Add New POI"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
        {isLoading && (
          <div className="text-center py-8 text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-creator-accent mx-auto"></div>
            <p className="mt-2 text-sm">Loading your POIs...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-4 text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-sm">{error}</p>
          </div>
        )}

        {!isLoading && !error && pois.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No POIs yet</p>
            <p className="text-xs mt-1">Click + to create your first POI</p>
          </div>
        )}

        {!isLoading && !error && pois.map((poi) => (
          <button
            key={poi.id}
            onClick={() => onSelectPOI(poi)}
            className="w-full text-left p-3 rounded-lg border border-creator-border dark:border-gray-700 hover:border-creator-accent dark:hover:border-creator-accent hover:shadow-md transition-all bg-white dark:bg-gray-800"
          >
            <div className="flex items-start space-x-3">
              {/* Thumbnail or Icon */}
              <div className="w-12 h-12 rounded-lg bg-creator-bg dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-creator-accent" />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm text-creator-text dark:text-gray-100 truncate">
                  {poi.name}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                  {poi.city || poi.country || 'Unknown location'}
                </p>
                
                {/* Status Badge */}
                <div className="flex items-center space-x-1 mt-1.5">
                  {getStatusIcon(poi.validation_status)}
                  <span className={`text-xs ${
                    poi.validation_status === 'VALIDATED' ? 'text-green-600' :
                    poi.validation_status === 'PENDING' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {getStatusText(poi.validation_status)}
                  </span>
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Footer Info */}
      {!isLoading && pois.length > 0 && (
        <div className="mt-4 pt-4 border-t border-creator-border dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {pois.length} POI{pois.length !== 1 ? 's' : ''} created
          </p>
        </div>
      )}
    </div>
  );
};

export default MyPOIsPanel;
